/* eslint-disable @typescript-eslint/no-unused-expressions */
import { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Typography } from 'components/Contentful'
//import { AchieveLink } from 'components/AchieveLink/AchieveLink'
import useMobileNavState from 'hooks/useMobileNavState'
import useActiveNavItemIndex from 'hooks/useActiveNavItemIndex'
import { useViewportSmallerThan } from 'utils/viewport'
import { BREAKPOINTS } from 'constants/viewport'
import useIsSsr from 'hooks/useIsSsr'
import SubNavMenu from './SubNavMenu'
import ChevronDown from 'components/Svgs/ChevronDown.js'
import classNames from 'classnames'
import { FdrPage, AchievePage } from '@/utils/environmentConfig'

const condensedHeight = 0

/**
 * @param {{
 *   disabledRoutes: []
 *  currentPathname: string
 *  itemIndex: number
 *  label: string
 *  subNavMenus: ?[]
 *  testid: string
 *  url: ?string
 * }} props
 */
export default function MainNavItem({
  currentPathname,
  itemIndex,
  label,
  subNavMenus,
  testid,
  url,
  disabledRoutes,
  disabledElements,
  titleVariant = 'bodyLg',
  titleMobileVariant = 'bodyLg',
  titleClassName,
  subNavClassName,
  subNavTitleClassName,
  siteEnv,
  onItemClick,
  isAchievePro,
}) {
  const titleRef = useRef(null)
  const subNavRef = useRef(null)

  const [activeIndex, setActiveIndex] = useActiveNavItemIndex()
  const [, setMobileNavOpen] = useMobileNavState()

  // Tracks the expanded height of the mobile sub nav
  const [expandedHeight, setExpandedHeight] = useState(null)

  const hasSubMenus = Boolean(subNavMenus && subNavMenus.length)
  const isActive = activeIndex === itemIndex
  const isSsr = useIsSsr()

  const isMobile = useViewportSmallerThan(BREAKPOINTS.lg)

  // Effect captures the height of the items expanded state. Re-fire when either
  // refs or the number of sub menus change
  useEffect(() => {
    if (!isMobile) {
      // Clear the mobile item heights when the window size is desktop
      expandedHeight && setExpandedHeight(null)
      return
    }

    // Set the expanded height if one isn't saved yet
    !expandedHeight && setExpandedHeight(subNavRef?.current?.offsetHeight)
  }, [expandedHeight, itemIndex, titleRef, subNavRef, hasSubMenus, isMobile])

  /**
   * Handles the mouse hover event to activate the main nav item and sub nav menus
   * @param {number} itemIndex
   */
  const handleHover = (itemIndex) => {
    // Don't use focus on small devices, that will be handled with onTouchEnd events
    !isMobile && setActiveIndex(itemIndex)
  }

  /**
   * Handles mouse click event and pressing the enter key
   * @param {object} e Synthetic click event
   * @param {number} clickedIndex Index of the clicked item
   */
  const handleClick = (e, clickedIndex) => {
    if (hasSubMenus) {
      return handleItemWithMenusClick(e, clickedIndex)
    }
    handleItemClick()
  }

  /**
   * Handles a click event on a main nav item that contains sub nav menus
   * @param {object} e Synthetic click event
   * @param {number} parentItemIndex Index of the clicked parent nav item
   */
  const handleItemWithMenusClick = (e, parentItemIndex) => {
    // Prevent default click behavior on the <a> tag
    e.preventDefault()

    // Clicking on the active main item closes its sub menu in the mobile nav
    if (isMobile && parentItemIndex === activeIndex) {
      return setActiveIndex()
    }
    // Clicking on a non-active main nav item activates it
    return setActiveIndex(parentItemIndex)
  }

  /**
   * Handles clicking on a main nav item that doesn't have sub nav menus
   * - scroll the window to the top for mobile view
   * - mobile nav should be dismissed
   * - active item should be cleared
   */
  const handleItemClick = () => {
    !isSsr && isMobile && window.scrollTo({ top: 0, behavior: 'instant' })
    setMobileNavOpen()
    setActiveIndex()
  }

  /**
   * Determines the expanded of condensed height of the mobile sub nav. Defaults
   * to auto on the server and in the desktop design viewport
   *
   * @returns {string} Height of the mobile sub nav or 'auto'
   */
  const getSubNavHeight = () => {
    let subNavHeight = 'auto'

    if (!isMobile || !expandedHeight) {
      return subNavHeight
    }

    subNavHeight = isActive ? `${expandedHeight}px` : `${condensedHeight}px`
    return subNavHeight
  }

  /**
   * Item title a tag wrapped with `next/link` whe the main item has a url prop
   */
  const getItemTitle = () => {
    let ItemTitle = (
      <>
        <div className={'flex items-center justify-between'}>
          <Typography
            className={classNames(
              AchievePage === siteEnv
                ? 'items-center text-[#1d252f] max-lg:group-data-[mobile-nav-open=true]:text-[#F8F9FC] flex h-[50px] justify-between [transition:250ms_cubic-bezier(0.4,0,0.2,1)] lg:h-full lg:overflow-visible lg:relative'
                : 'items-center text-[#1d252f] flex h-[50px] justify-between [transition:250ms_cubic-bezier(0.4,0,0.2,1)] lg:h-full lg:overflow-visible lg:relative',
              '[.main-nav-item[data-selected=true]_&]:text-blue-500 lg:[.main-nav-item[data-selected=true]_&]:text-blue-500 [.main-nav-item[data-selected=true]_&]:after:transition-[bottom] [.main-nav-item[data-selected=true]_&]:after:duration-[250ms_cubic-bezier(0.4,0,0.2,1)] [.main-nav-item[data-selected=true]_&]:after:ease-[var.$default-animation-timing] lg:[.main-nav-item[data-active=true]_&]:after:bg-blue-500 lg:[.main-nav-item[data-selected=true]_&]:after:bg-blue-500 lg:[.main-nav-item[data-selected=true]_&]:after:rounded lg:[.main-nav-item[data-selected=true]_&]:after:-bottom-5 lg:[.main-nav-item[data-selected=true]_&]:after:content-[""] lg:[.main-nav-item[data-selected=true]_&]:after:h-1 lg:[.main-nav-item[data-selected=true]_&]:after:absolute lg:[.main-nav-item[data-selected=true]_&]:after:w-full',
              'lg:[.main-nav-item[data-selected=true]_&]:group-data-[condensed=true]:after:bottom-[-10px]',
              'font-medium',
              siteEnv === AchievePage &&
                'max-lg:[.main-nav-item[data-selected=true]_&]:group-data-[mobile-nav-open=true]:text-white',
              siteEnv === FdrPage ? 'lg:font-medium' : 'lg:font-bold',
              titleClassName
            )}
            variant={titleVariant}
            mobileVariant={titleMobileVariant}
            component="span"
            breakpoint="lg"
            responsive={true}
            content={label}
            data-nav="item-title"
          />
          {hasSubMenus && (
            <span
              className={classNames(
                'transition-[250ms_cubic-bezier(0.4,0,0.2,1)]',
                '[.main-nav-item[data-active=true]_&]:rotate-180 lg:[.main-nav-item[data-active=true]_&]:rotate-0',
                'lg:hidden'
              )}
            >
              <ChevronDown
                title={label}
                width={13}
                height={8}
                stroke={siteEnv === AchievePage ? '#F8F9FC' : '#1F1F23'}
              />
            </span>
          )}
        </div>
      </>
    )

    return url ? (
      <a
        href={url}
        aria-haspopup={hasSubMenus ? 'true' : 'false'}
        role="button"
        aria-expanded="false"
        onClick={(e) => {
          onItemClick?.(e, { url, label })
          if (e.defaultPrevented) return
          handleClick(e, itemIndex)
        }}
        ref={titleRef}
      >
        {ItemTitle}
      </a>
    ) : (
      <button
        aria-haspopup={hasSubMenus ? 'true' : 'false'}
        role="button"
        aria-expanded="false"
        className={classNames(
          siteEnv === AchievePage
            ? 'bg-white max-lg:group-data-[mobile-nav-open=true]:bg-black w-full border-0 font-[inherit]'
            : 'bg-white w-full border-0 font-[inherit]',
          siteEnv === AchievePage
            ? '[.main-nav-item>&]:cursor-pointer [.main-nav-item>&]:block [.main-nav-item>&]:no-underline [.main-nav-item>&]:transition-[250ms_cubic-bezier(0.4,0,0.2,1)] [.main-nav-item>&]:px-0 [.main-nav-item>&]:py-0'
            : '[.main-nav-item>&]:cursor-pointer [.main-nav-item>&]:block [.main-nav-item>&]:no-underline [.main-nav-item>&]:transition-[250ms_cubic-bezier(0.4,0,0.2,1)] [.main-nav-item>&]:px-4 [.main-nav-item>&]:py-0',
          'last:[.main-nav-item>&]:border-b-0',
          'lg:[.main-nav-item>&]:items-center lg:[.main-nav-item>&]:flex lg:[.main-nav-item>&]:py-0 lg:[.main-nav-item>&]:border-0',
          'lg:[.main-nav-item>&]:[&_span]:group-data-[condensed=true]:text-[15px]',
          'lg:[.main-nav-item>&]:[&_a_span]:group-data-[condensed=true]:text-[15px]',
          siteEnv === FdrPage ? 'lg:[.main-nav-item>&]:px-2' : 'lg:[.main-nav-item>&]:px-4'
        )}
        onClick={(e) => {
          handleClick(e, itemIndex)
        }}
      >
        {ItemTitle}
      </button>
    )
  }

  /**
   * Memoized value to determine if the Main Item should be data-selected="true" by checking its
   * child menus and items for a url match with currentPathname.
   */
  const isSelectedParentItem = useMemo(() => {
    if (!subNavMenus || !Array.isArray(subNavMenus)) {
      return false
    }

    // Determine the index of the subMenu with a child item route match
    const selectedSubMenuIndex = subNavMenus.findIndex(({ menuItems = [] }) => {
      const subUrlMatch = menuItems
        .filter((item) => item)
        .findIndex(({ url }) => currentPathname.startsWith(url))
      return subUrlMatch >= 0
    })

    return selectedSubMenuIndex >= 0
  }, [currentPathname, subNavMenus])

  return (
    <li
      className={classNames(
        siteEnv === AchievePage
          ? 'group main-nav-item lg:h-full lg:items-stretch lg:flex lg:border-0'
          : 'group main-nav-item border-b-neutral-75 border-b border-solid border-x-0 border-t-0 lg:h-full lg:items-stretch lg:flex lg:border-0',
        '[&>a]:cursor-pointer [&>a]:block [&_a]:no-underline [&>a]:transition-[250ms_cubic-bezier(0.4,0,0.2,1)] [&>a]:px-4 [&>a]:py-0 lg:[&>a]:items-center lg:[&>a]:flex lg:[&>a]:px-4 lg:[&>a]:py-0 lg:[&>a]:border-0',
        '[&>a:last-child]:border-b-0',
        '[&_a_span]:group-data-[condensed=true]:in-[.main-nav]:size-[15px]'
      )}
      data-item-index={itemIndex}
      data-active={isActive}
      data-selected={!isAchievePro && (currentPathname === url || isSelectedParentItem)}
      data-testid={testid}
      onMouseEnter={() => handleHover(itemIndex)}
      onMouseLeave={() => handleHover()}
    >
      {getItemTitle()}
      {hasSubMenus && (
        <div
          className={classNames(
            siteEnv === AchievePage
              ? 'overflow-hidden transition-all duration-600 ease-in-out'
              : 'overflow-hidden transition-all duration-[250ms_cubic-bezier(0.4,0,0.2,1)]',
            siteEnv === AchievePage
              ? 'lg:bg-white max-lg:group-data-[mobile-nav-open=true]:bg-black lg:border-solid lg:border-neutral-75 lg:rounded-md lg:shadow-[0_1px_14px_rgb(0_0_0/12%)] lg:hidden lg:absolute lg:translate-x-[-24px] lg:translate-y-[96px] lg:[&_a]:[text-decoration:none]'
              : 'lg:bg-white lg:border-solid lg:border-neutral-75 lg:rounded-md lg:shadow-[0_1px_14px_rgb(0_0_0/12%)] lg:hidden lg:absolute lg:translate-x-[-24px] lg:translate-y-[96px] lg:[&_a]:[text-decoration:none]',
            'lg:group-data-[active=true]:flex',
            'lg:[&_ul]:p-0',
            'lg:group-data-[condensed=true]:in-[.main-nav]:translate-x-[-24px] lg:group-data-[condensed=true]:in-[.main-nav]:translate-y-[64px]',
            'lg:data-[nav="sub-nav-container"]:group-data-[condensed=true]:group-data-[product="achieve"]:translate-x-[-24px] lg:data-[nav="sub-nav-container"]:group-data-[condensed=true]:group-data-[product="achieve"]:translate-y-[64px]',
            subNavClassName
          )}
          style={{ height: getSubNavHeight() }}
          ref={subNavRef}
          data-testid={testid ? `${testid}-subnav` : `header-menu-${itemIndex}-subnav`}
          data-nav="sub-nav-container"
        >
          {subNavMenus.map(({ menuTitle, menuItems }) => (
            <SubNavMenu
              currentPathname={currentPathname}
              items={menuItems.filter((item) => item)}
              key={menuTitle}
              mainNavLabel={label}
              title={menuTitle}
              disabledRoutes={disabledRoutes}
              disabledElements={disabledElements}
              subNavTitleClassName={subNavTitleClassName}
            />
          ))}
        </div>
      )}
    </li>
  )
}

MainNavItem.propTypes = {
  currentPathname: PropTypes.string.isRequired,
  disabledRoutes: PropTypes.array,
  itemIndex: PropTypes.number,
  label: PropTypes.string.isRequired,
  subNavMenus: PropTypes.array,
  testid: PropTypes.string.isRequired,
  url: PropTypes.string,
  onItemClick: PropTypes.func,
}
